import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { Button } from '../ui/button';
import logoLong from '../../assets/diligenshub-logo.svg';
import { LOGIN_URL } from '../../lib/links';
import { ALL_USE_CASES, SOLUTION_GROUPS, SOLUTIONS_MENU, isSolutionsPath } from '../../content/solutions/nav';

const navLinks = [
  { name: 'Product', href: '/product' },
  { name: 'AI Features', href: '/ai-features' },
  { name: 'Security', href: '/security' },
  { name: 'How It Works', href: '/how-it-works' },
];

const slug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

// Active page: navy underline on the header's bottom edge, the app's tab idiom.
const tabClass = (active) =>
  `inline-flex items-center border-b-2 -mb-px text-sm font-medium transition-colors duration-150 ${
    active ? 'border-primary-900 text-primary-900' : 'border-transparent text-neutral-600 hover:text-primary-900'
  }`;

// The Solutions menu: opens on click (not hover), closes on Escape, a click
// outside, focus leaving it, or navigation. Its links follow the button in
// the tab order, so the keyboard reaches them once it's open.
const SolutionsMenu = ({ pathname }) => {
  const [open, setOpen] = useState(false);
  const wrapper = useRef(null);
  const button = useRef(null);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
      }
    };
    const onPointer = (event) => {
      if (!wrapper.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  const onBlur = (event) => {
    if (event.relatedTarget && !wrapper.current?.contains(event.relatedTarget)) setOpen(false);
  };

  return (
    <div ref={wrapper} className="relative flex items-stretch" onBlur={onBlur}>
      <button
        ref={button}
        type="button"
        aria-expanded={open}
        aria-controls="solutions-menu"
        onClick={() => setOpen((value) => !value)}
        className={`${tabClass(isSolutionsPath(pathname))} gap-1`}
        data-testid="nav-solutions-button"
      >
        {SOLUTIONS_MENU}
        <ChevronDown className={`h-4 w-4 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <div
          id="solutions-menu"
          className="absolute left-1/2 top-full z-50 mt-px w-[30rem] -translate-x-1/2 rounded border border-neutral-200 bg-white p-6"
          data-testid="solutions-menu"
        >
          <div className="grid grid-cols-2 gap-8">
            {SOLUTION_GROUPS.map((group) => (
              <div key={group.label}>
                <p className="label-uppercase mb-3 text-neutral-500" id={`solutions-${slug(group.label)}`}>
                  {group.label}
                </p>
                <ul className="space-y-2" aria-labelledby={`solutions-${slug(group.label)}`}>
                  {group.links.map((link) => (
                    <li key={link.to}>
                      <Link
                        to={link.to}
                        aria-current={pathname === link.to ? 'page' : undefined}
                        className={`text-sm ${pathname === link.to ? 'font-medium text-primary-900' : 'text-neutral-600 hover:text-primary-900'}`}
                        onClick={() => setOpen(false)}
                        data-testid={`solutions-link-${slug(link.label)}`}
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-5 border-t border-neutral-200 pt-4">
            <Link
              to={ALL_USE_CASES.to}
              aria-current={pathname === ALL_USE_CASES.to ? 'page' : undefined}
              className="text-sm font-medium text-primary-900 hover:underline underline-offset-4"
              onClick={() => setOpen(false)}
              data-testid="solutions-link-all-use-cases"
            >
              {ALL_USE_CASES.label}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { pathname } = useLocation();

  const isActive = (href) => pathname === href;

  useEffect(() => setMobileMenuOpen(false), [pathname]);

  const mobileLink = (to, label, testId) => (
    <Link
      key={to}
      to={to}
      aria-current={isActive(to) ? 'page' : undefined}
      className={`block py-2.5 text-sm font-medium ${isActive(to) ? 'text-primary-900' : 'text-neutral-600'}`}
      onClick={() => setMobileMenuOpen(false)}
      data-testid={testId}
    >
      {label}
    </Link>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-neutral-200" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <img src={logoLong} alt="DiligensHub" className="h-7 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-stretch self-stretch space-x-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                aria-current={isActive(link.href) ? 'page' : undefined}
                className={tabClass(isActive(link.href))}
                data-testid={`nav-link-${slug(link.name)}`}
              >
                {link.name}
              </Link>
            ))}
            <SolutionsMenu pathname={pathname} />
          </nav>

          <div className="hidden lg:flex items-center space-x-5">
            <a
              href={LOGIN_URL}
              className="text-sm font-medium text-neutral-600 hover:text-primary-900 transition-colors duration-150"
              data-testid="header-login-link"
            >
              Log In
            </a>
            <Button asChild data-testid="header-request-info-btn">
              <Link to="/request-info">Request Information</Link>
            </Button>
          </div>

          <button
            type="button"
            className="lg:hidden p-2 -mr-2 text-neutral-600 hover:text-primary-900"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div
          className="lg:hidden max-h-[calc(100vh-4rem)] overflow-y-auto bg-white border-t border-neutral-200"
          data-testid="mobile-menu"
        >
          <div className="px-4 py-3">
            {navLinks.map((link) => mobileLink(link.href, link.name, `mobile-nav-link-${slug(link.name)}`))}
            {/* Solutions: the menu's groups, expanded as plain lists */}
            <div className="mt-3 border-t border-neutral-200 pt-3">
              {SOLUTION_GROUPS.map((group) => (
                <div key={group.label} className="pb-2">
                  <p className="label-uppercase pt-2 pb-1 text-neutral-500">{group.label}</p>
                  {group.links.map((link) => mobileLink(link.to, link.label, `mobile-solutions-link-${slug(link.label)}`))}
                </div>
              ))}
              {mobileLink(ALL_USE_CASES.to, ALL_USE_CASES.label, 'mobile-solutions-link-all-use-cases')}
            </div>
            <div className="mt-3 pt-3 border-t border-neutral-200 space-y-3">
              <a
                href={LOGIN_URL}
                className="block py-2 text-sm font-medium text-neutral-600"
                data-testid="mobile-login-link"
              >
                Log In
              </a>
              <Button asChild className="w-full" data-testid="mobile-request-info-btn">
                <Link to="/request-info" onClick={() => setMobileMenuOpen(false)}>
                  Request Information
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
